// tabs/QuestionsTab.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Chip,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Quiz as QuizIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

import {
  SectionCard,
  SectionTitle,
  StyledTextField,
  StyledSelect,
  ActionButton,
  EmptyState,
  honeyColors,
} from '../styled';

const QUESTION_TYPES = [
  { value: 'TX', label: 'Text (Short Answer)' },
  { value: 'TA', label: 'Textarea (Long Answer)' },
  { value: 'SC', label: 'Single Choice' },
  { value: 'MC', label: 'Multiple Choice' },
  { value: 'BO', label: 'Yes/No' },
  { value: 'NU', label: 'Number' },
  { value: 'EM', label: 'Email' },
  { value: 'UR', label: 'URL' },
];

const getQuestionTypeText = (type) => {
  const found = QUESTION_TYPES.find(t => t.value === type);
  return found ? found.label : type;
};

const QuestionCard = ({ question, index, onEdit, onDelete, onReorder }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [draggedOver, setDraggedOver] = useState(false);

  const handleDragStart = (e) => {
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDraggedOver(true);
  };

  const handleDragLeave = () => {
    setDraggedOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
    if (draggedIndex !== index) {
      onReorder(draggedIndex, index);
    }
    setDraggedOver(false);
  };

  if (question.is_active === false) {
    return null; // Don't render deleted questions
  }

  return (
    <Card
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        mb: 2,
        borderRadius: '12px',
        border: `1px solid ${draggedOver ? honeyColors.primary : honeyColors.border}`,
        boxShadow: draggedOver ? honeyColors.shadowLight : 'none',
        opacity: isDragging ? 0.5 : 1,
        transform: draggedOver ? 'translateY(-2px)' : 'none',
        transition: 'all 0.2s ease',
        backgroundColor: honeyColors.surface,
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="flex-start" gap={2}>
          <DragIcon
            sx={{
              color: honeyColors.textMuted,
              cursor: 'grab',
              mt: 0.5,
              '&:active': { cursor: 'grabbing' },
            }}
          />
          
          <Box flex={1}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1rem' }}>
              {question.question_text}
            </Typography>
            
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Chip
                label={getQuestionTypeText(question.question_type)}
                size="small"
                sx={{
                  backgroundColor: honeyColors.surfaceHoney,
                  color: honeyColors.textSecondary,
                  fontWeight: 500,
                }}
              />
              
              {question.is_required && (
                <Chip
                  label="Required"
                  size="small"
                  sx={{
                    backgroundColor: honeyColors.warning,
                    color: '#fff',
                    fontWeight: 600,
                  }}
                />
              )}
              
              {question.choices && question.choices.length > 0 && (
                <Typography variant="body2" color="textSecondary">
                  {question.choices.length} options
                </Typography>
              )}
            </Box>
            
            {question.choices && question.choices.length > 0 && (
              <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                {question.choices.map((choice, i) => (
                  <Chip
                    key={i}
                    label={choice}
                    size="small"
                    variant="outlined"
                    sx={{ borderColor: honeyColors.border }}
                  />
                ))}
              </Box>
            )}
          </Box>
          
          <Box display="flex" gap={0.5}>
            <IconButton size="small" onClick={() => onEdit(question)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => onDelete(question.id)} sx={{ color: honeyColors.error }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const QuestionDialog = ({ open, question, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    question_text: '',
    question_type: 'TX',
    is_required: true,
    choices: [],
    max_length: null,
    min_value: null,
    max_value: null,
  });
  const [newChoice, setNewChoice] = useState('');
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (question) {
      setFormData({
        question_text: question.question_text || '',
        question_type: question.question_type || 'TX',
        is_required: question.is_required !== undefined ? question.is_required : true,
        choices: question.choices || [],
        max_length: question.max_length || null,
        min_value: question.min_value || null,
        max_value: question.max_value || null,
      });
    } else {
      setFormData({
        question_text: '',
        question_type: 'TX',
        is_required: true,
        choices: [],
        max_length: null,
        min_value: null,
        max_value: null,
      });
    }
    setErrors({});
  }, [question, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    
    // Clear choices when switching away from choice-based types
    if (field === 'question_type' && !['SC', 'MC'].includes(value)) {
      setFormData(prev => ({ ...prev, choices: [] }));
    }
  };

  const handleAddChoice = () => {
    if (newChoice.trim() && !formData.choices.includes(newChoice.trim())) {
      setFormData(prev => ({
        ...prev,
        choices: [...prev.choices, newChoice.trim()],
      }));
      setNewChoice('');
    }
  };

  const handleRemoveChoice = (choice) => {
    setFormData(prev => ({
      ...prev,
      choices: prev.choices.filter(c => c !== choice),
    }));
  };

  const handleSave = () => {
    const newErrors = {};
    
    if (!formData.question_text.trim()) {
      newErrors.question_text = 'Question text is required';
    }
    
    if (['SC', 'MC'].includes(formData.question_type) && formData.choices.length === 0) {
      newErrors.choices = 'At least one choice is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onSave({
      ...formData,
      id: question?.id,
    });
    onClose();
  };

  const isChoiceBased = ['SC', 'MC'].includes(formData.question_type);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {question ? 'Edit Question' : 'Add New Question'}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <StyledTextField
            label="Question Text"
            value={formData.question_text}
            onChange={(e) => handleChange('question_text', e.target.value)}
            error={!!errors.question_text}
            helperText={errors.question_text}
            multiline
            rows={2}
            fullWidth
            required
            sx={{ mb: 3 }}
          />
          
          <Box display="flex" gap={3} mb={3}>
            <Box flex={1}>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 600 }}>
                Question Type
              </Typography>
              <StyledSelect
                value={formData.question_type}
                onChange={(e) => handleChange('question_type', e.target.value)}
                fullWidth
              >
                {QUESTION_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </StyledSelect>
            </Box>
            
            <Box display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_required}
                    onChange={(e) => handleChange('is_required', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: honeyColors.primary,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: honeyColors.primary,
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>Required</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Members must answer this
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Box>
          
          {isChoiceBased && (
            <Box mb={3}>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 600 }}>
                Answer Choices
              </Typography>
              
              {formData.choices.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                  {formData.choices.map((choice, i) => (
                    <Chip
                      key={i}
                      label={choice}
                      onDelete={() => handleRemoveChoice(choice)}
                      sx={{
                        backgroundColor: honeyColors.surfaceHoney,
                        '& .MuiChip-deleteIcon': {
                          color: honeyColors.textSecondary,
                          '&:hover': { color: honeyColors.error },
                        },
                      }}
                    />
                  ))}
                </Box>
              )}
              
              <Box display="flex" gap={2}>
                <StyledTextField
                  label="New Choice"
                  value={newChoice}
                  onChange={(e) => setNewChoice(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddChoice();
                    }
                  }}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddChoice}
                  disabled={!newChoice.trim()}
                  sx={{
                    borderColor: honeyColors.border,
                    color: honeyColors.textSecondary,
                  }}
                >
                  Add
                </Button>
              </Box>
              
              {errors.choices && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  {errors.choices}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <ActionButton variant="contained" onClick={handleSave}>
          {question ? 'Update Question' : 'Add Question'}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

const QuestionsTab = ({
  questions,
  requiresQuestions,
  saving,
  onFormChange,
  onQuestionAdd,
  onQuestionUpdate,
  onQuestionDelete,
  onQuestionReorder,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const handleOpenDialog = (question = null) => {
    setEditingQuestion(question);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingQuestion(null);
  };

  const handleSaveQuestion = (questionData) => {
    if (editingQuestion) {
      onQuestionUpdate(editingQuestion.id, questionData);
    } else {
      onQuestionAdd(questionData);
    }
  };

  const handleReorder = (fromIndex, toIndex) => {
    const reordered = [...questions];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    
    const withNewOrder = reordered.map((q, idx) => ({
      ...q,
      order: idx,
    }));
    
    onQuestionReorder(withNewOrder);
  };

  const activeQuestions = questions.filter(q => q.is_active !== false);

  return (
    <Box>
      {/* Toggle Section */}
      <SectionCard sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <QuizIcon sx={{ color: honeyColors.primary, fontSize: 32 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Admission Questions
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Require new members to answer questions before joining
                </Typography>
              </Box>
            </Box>
            
            <FormControlLabel
              control={
                <Switch
                  checked={requiresQuestions}
                  onChange={(e) => onFormChange('requires_admission_questions', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: honeyColors.primary,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: honeyColors.primary,
                    },
                  }}
                />
              }
              label={requiresQuestions ? 'Enabled' : 'Disabled'}
            />
          </Box>
        </CardContent>
      </SectionCard>

      {requiresQuestions && (
        <>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Questions ({activeQuestions.length})
            </Typography>
            <ActionButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Question
            </ActionButton>
          </Box>

          {/* Questions List */}
          {activeQuestions.length > 0 ? (
            <>
              {activeQuestions.map((question, index) => (
                <QuestionCard
                  key={question.id}
                  question={question}
                  index={index}
                  onEdit={() => handleOpenDialog(question)}
                  onDelete={onQuestionDelete}
                  onReorder={handleReorder}
                />
              ))}
              
              <Box
                sx={{
                  backgroundColor: honeyColors.surfaceHoney,
                  borderRadius: '8px',
                  padding: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: `1px solid ${honeyColors.border}`,
                }}
              >
                <DragIcon sx={{ color: honeyColors.textMuted }} />
                <Typography variant="body2" color="textSecondary">
                  Drag questions to reorder them
                </Typography>
              </Box>
            </>
          ) : (
            <EmptyState>
              <QuizIcon sx={{ fontSize: 64, color: honeyColors.border, mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No Admission Questions Yet
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Add questions that new members need to answer when requesting to join
              </Typography>
              <ActionButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Add First Question
              </ActionButton>
            </EmptyState>
          )}
        </>
      )}

      {/* Question Dialog */}
      <QuestionDialog
        open={dialogOpen}
        question={editingQuestion}
        onClose={handleCloseDialog}
        onSave={handleSaveQuestion}
      />
    </Box>
  );
};

export default QuestionsTab;
